import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Stack,
  Typography,
  Alert,
  CircularProgress,
  Box,
  InputAdornment,
  IconButton
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';

const CoinbaseProModal = ({ 
  open, 
  onClose, 
  onSave, 
  isLoading = false,
  error = null,
  editingSource
}) => {
  const [formData, setFormData] = useState({
    display_name: '',
    source_name: 'coinbase_pro',
    metadata: {
      api_key: '',
      api_secret: '',
      api_passphrase: ''
    }
  });
  
  const [showSecret, setShowSecret] = useState(false);
  const [showPassphrase, setShowPassphrase] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});

  // Pre-populate form when editing
  useEffect(() => {
    if (editingSource) {
      setFormData({
        display_name: editingSource.display_name || '',
        source_name: editingSource.source_name || 'coinbase_pro',
        metadata: {
          api_key: editingSource.metadata?.api_key || '',
          api_secret: editingSource.metadata?.api_secret || '',
          api_passphrase: editingSource.metadata?.api_passphrase || ''
        }
      });
    } else {
      // Reset form for new source
      setFormData({
        display_name: '',
        source_name: 'coinbase_pro',
        metadata: {
          api_key: '',
          api_secret: '',
          api_passphrase: ''
        }
      });
    }
    // Clear field errors when source changes
    setFieldErrors({});
  }, [editingSource, open]);

  // Handle input changes
  const handleInputChange = (field, value) => {
    if (field.startsWith('metadata.')) {
      const metadataField = field.replace('metadata.', '');
      setFormData(prev => ({
        ...prev,
        metadata: {
          ...prev.metadata,
          [metadataField]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }));
    }
    
    // Clear field error when user starts typing
    if (fieldErrors[field]) {
      setFieldErrors(prev => ({
        ...prev,
        [field]: undefined
      }));
    }
  };

  // Form validation
  const validateForm = () => {
    const errors = {};
    
    if (!formData.display_name.trim()) {
      errors.display_name = 'Display name is required';
    }
    
    if (!formData.metadata.api_key.trim()) {
      errors['metadata.api_key'] = 'API Key is required';
    }
    
    if (!formData.metadata.api_secret.trim()) {
      errors['metadata.api_secret'] = 'API Secret is required';
    }
    
    if (!formData.metadata.api_passphrase.trim()) {
      errors['metadata.api_passphrase'] = 'API Passphrase is required';
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle form submission
  const handleSubmit = () => {
    if (!validateForm()) {
      return;
    }

    onSave(formData);
  };

  // Handle cancel/close
  const handleClose = () => {
    if (!isLoading) {
      onClose();
      // Reset form data when closing
      setFormData({
        display_name: '',
        source_name: 'coinbase_pro',
        metadata: {
          api_key: '',
          api_secret: '',
          api_passphrase: ''
        }
      });
      setFieldErrors({});
      setShowSecret(false);
      setShowPassphrase(false);
    }
  };

  const isFormValid = formData.display_name.trim() && 
                     formData.metadata.api_key.trim() && 
                     formData.metadata.api_secret.trim() && 
                     formData.metadata.api_passphrase.trim();

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      aria-labelledby="coinbase-pro-dialog-title"
    >
      <DialogTitle id="coinbase-pro-dialog-title">
        {editingSource ? 'Edit Coinbase Pro Source' : 'Add Coinbase Pro Source'}
      </DialogTitle>
      
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        <Stack spacing={3} sx={{ mt: 1 }}>
          <TextField
            label="Display Name"
            fullWidth
            value={formData.display_name}
            onChange={(e) => handleInputChange('display_name', e.target.value)}
            error={!!fieldErrors.display_name}
            helperText={fieldErrors.display_name || 'A friendly name for this connection'}
            disabled={isLoading}
            placeholder="e.g., My Coinbase Pro Account"
          />

          <TextField
            label="API Key"
            fullWidth
            value={formData.metadata.api_key}
            onChange={(e) => handleInputChange('metadata.api_key', e.target.value)}
            error={!!fieldErrors['metadata.api_key']}
            helperText={fieldErrors['metadata.api_key'] || 'Your Coinbase Pro API Key'}
            disabled={isLoading}
            placeholder="Enter your API Key"
          />

          <TextField
            label="API Secret"
            fullWidth
            type={showSecret ? 'text' : 'password'}
            value={formData.metadata.api_secret}
            onChange={(e) => handleInputChange('metadata.api_secret', e.target.value)}
            error={!!fieldErrors['metadata.api_secret']}
            helperText={fieldErrors['metadata.api_secret'] || 'Your Coinbase Pro API Secret'}
            disabled={isLoading}
            placeholder="Enter your API Secret"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowSecret(!showSecret)}
                    edge="end"
                    disabled={isLoading}
                  >
                    {showSecret ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              )
            }}
          />

          <TextField
            label="API Passphrase"
            fullWidth
            type={showPassphrase ? 'text' : 'password'}
            value={formData.metadata.api_passphrase}
            onChange={(e) => handleInputChange('metadata.api_passphrase', e.target.value)}
            error={!!fieldErrors['metadata.api_passphrase']}
            helperText={fieldErrors['metadata.api_passphrase'] || 'Your Coinbase Pro API Passphrase'}
            disabled={isLoading}
            placeholder="Enter your API Passphrase"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPassphrase(!showPassphrase)}
                    edge="end"
                    disabled={isLoading}
                  >
                    {showPassphrase ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              )
            }}
          />
        </Stack>
      </DialogContent>
      
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained"
          disabled={isLoading || !isFormValid}
          startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : null}
        >
          {isLoading ? (editingSource ? 'Updating...' : 'Adding...') : (editingSource ? 'Update Source' : 'Add Source')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

CoinbaseProModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
  error: PropTypes.string,
  editingSource: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    display_name: PropTypes.string,
    source_name: PropTypes.string,
    metadata: PropTypes.shape({
      api_key: PropTypes.string,
      api_secret: PropTypes.string,
      api_passphrase: PropTypes.string
    })
  })
};

CoinbaseProModal.defaultProps = {
  isLoading: false,
  error: null,
  editingSource: null
};

export default CoinbaseProModal; 
